import { useEffect, useState } from "react";
import api from "../lib/api";
import { CaretDown, CaretRight, ShieldCheck, Question } from "@phosphor-icons/react";

// A compact "why does/doesn't this apply?" card for the Standards Library.
// Reads /api/standards/applicability-summary and shows:
//   - profile chip strip (registered functions, impact ratings, no_cip_assets flag)
//   - the CIP scoping rule the profile derives ("Low only", "No CIP assets", etc.)
//   - per-family roll-up (CIP: 3/29 · PRC: 4/21 · MOD: 2/6 …)
//   - collapsible per-standard state with a one-line reason
export default function ApplicabilityScopingCard({ onChange }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [famExpanded, setFamExpanded] = useState({});
  const load = () => api.get("/standards/applicability-summary").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  // Give parent a re-load hook so it can trigger a refresh when the profile changes
  useEffect(() => { if (onChange) onChange(load); }, [onChange]);

  if (!data) return null;
  const p = data.profile || {};

  const chip = (v, tone = "slate") => {
    const tones = {
      slate: "border-slate-300 bg-slate-50 text-slate-700",
      indigo: "border-indigo-300 bg-indigo-50 text-indigo-700",
      amber: "border-amber-400 bg-amber-50 text-amber-800",
      green: "border-emerald-300 bg-emerald-50 text-emerald-700",
      red: "border-rose-300 bg-rose-50 text-rose-700",
    };
    return <span className={`border ${tones[tone]} px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide`}>{v}</span>;
  };

  const ruleTone = {
    full: "slate",
    low_only: "indigo",
    no_cip_002_only: "amber",
    no_cip_cat2: "red",
  }[(data.cip_rule || {}).code] || "slate";
  void ruleTone;

  return (
    <div className="border border-border bg-card" data-testid="applicability-scoping-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-border hover:bg-secondary/40"
        data-testid="scoping-card-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck size={16} weight="bold" className="text-primary shrink-0" />
          <span className="font-display font-bold text-sm">Applicability scoping — what applies to your entity</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span data-testid="scoping-totals" className="font-mono">
            {data.totals.standards_applicable}/{data.totals.standards_total} standards · {data.totals.requirements_applicable}/{data.totals.requirements_total} requirements
            {data.totals.requirements_future > 0 && <span className="ml-1 opacity-70">(+{data.totals.requirements_future} future)</span>}
          </span>
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </div>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {/* Profile chip strip */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="scoping-profile-strip">
            <span className="overline text-muted-foreground mr-1">Profile</span>
            {(p.registered_functions || []).length > 0 ? (
              (p.registered_functions || []).map((f) => chip(f, "indigo"))
            ) : (
              <span className="text-xs text-amber-700">no registered functions set — set them in Entity Profile</span>
            )}
            <span className="mx-2 text-muted-foreground text-xs">·</span>
            <span className="overline text-muted-foreground mr-1">Impact</span>
            {p.no_cip_assets
              ? chip("No NERC CIP Assets", "amber")
              : ((p.impact_ratings || []).length > 0
                  ? (p.impact_ratings || []).map((i) => chip(i, i === "high" ? "red" : i === "medium" ? "amber" : "green"))
                  : <span className="text-xs text-muted-foreground">not categorized yet</span>)}
            {(p.regional_entities || []).length > 0 && (
              <>
                <span className="mx-2 text-muted-foreground text-xs">·</span>
                <span className="overline text-muted-foreground mr-1">Region</span>
                {(p.regional_entities || []).map((r) => chip(r, "slate"))}
              </>
            )}
          </div>

          {/* CIP rule callout removed until ClaroShield Enterprise is complete */}

          {/* CFR overlay chips — show when at least one active matrix exists */}
          {(data.cfr?.active_matrices || 0) > 0 && (
            <div className="border border-border p-3" data-testid="scoping-cfr">
              <div className="flex items-center gap-1.5 mb-2">
                <ShieldCheck size={13} className="text-primary" />
                <span className="overline text-muted-foreground">
                  CFR overlay — {data.cfr.active_matrices} active {data.cfr.active_matrices === 1 ? "matrix" : "matrices"}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {data.cfr.routed_away > 0 && (
                  <span className="inline-flex items-center gap-1.5 border border-rose-300 bg-rose-50 text-rose-700 px-2 py-1 text-xs font-semibold rounded-none" data-testid="cfr-routed-away">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500" />
                    {data.cfr.routed_away} req{data.cfr.routed_away === 1 ? "" : "s"} routed away
                  </span>
                )}
                {data.cfr.routed_to_us > 0 && (
                  <span className="inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1 text-xs font-semibold rounded-none" data-testid="cfr-routed-to-us">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {data.cfr.routed_to_us} routed to us (sole)
                  </span>
                )}
                {data.cfr.shared_or_split > 0 && (
                  <span className="inline-flex items-center gap-1.5 border border-blue-300 bg-blue-50 text-blue-700 px-2 py-1 text-xs font-semibold rounded-none" data-testid="cfr-shared">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
                    {data.cfr.shared_or_split} shared / split
                  </span>
                )}
                {data.cfr.not_applicable > 0 && (
                  <span className="inline-flex items-center gap-1.5 border border-slate-300 bg-slate-50 text-slate-700 px-2 py-1 text-xs font-semibold rounded-none" data-testid="cfr-na">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
                    {data.cfr.not_applicable} N/A under CFR
                  </span>
                )}
                {(data.cfr.routed_away + data.cfr.routed_to_us + data.cfr.shared_or_split + data.cfr.not_applicable) === 0 && (
                  <span className="text-xs text-muted-foreground italic">
                    CFR matrices are active but no rows currently affect applicability (check that &ldquo;My Entity&rdquo; is set on each matrix).
                  </span>
                )}
              </div>
              <ul className="text-xs space-y-0.5">
                {data.cfr.matrices.map((m) => (
                  <li key={m.id} className="flex items-center gap-2 text-muted-foreground">
                    <span className="font-mono font-bold text-[10px] px-1.5 py-0.5 border border-border">{m.registered_function || "?"}</span>
                    <span className="truncate min-w-0">{m.title || "Untitled matrix"}</span>
                    <span className="shrink-0">· My Entity: <b>{m.my_entity_party || <span className="text-amber-700">not set</span>}</b></span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Wizard-answered gates (UVLS/RAS/etc.) */}
          {(data.answered_gates || []).length > 0 && (
            <div className="border border-border p-3" data-testid="scoping-gates">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Question size={13} className="text-muted-foreground" />
                <span className="overline text-muted-foreground">Applicability Wizard answers driving scope</span>
              </div>
              <ul className="text-xs space-y-1">
                {data.answered_gates.slice(0, 12).map((g, i) => (
                  <li key={`${g.standard_id}-${i}`} className="flex items-start gap-2">
                    <span className="font-mono text-[10px] uppercase font-bold px-1.5 py-0.5 border border-border shrink-0">{g.standard_id}</span>
                    <span className="min-w-0"><span className="text-muted-foreground">{g.text}</span> — <b>{g.answer ? "Yes" : "No"}</b></span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Family roll-up chips */}
          <div className="flex flex-wrap gap-2" data-testid="scoping-families">
            {(data.families || []).map((f) => {
              const applies = f.standards_applicable > 0;
              return (
                <button
                  key={f.family}
                  onClick={() => setFamExpanded((s) => ({ ...s, [f.family]: !s[f.family] }))}
                  data-testid={`scoping-family-${f.family}`}
                  className={`border px-2.5 py-1.5 text-xs font-semibold rounded-none inline-flex items-center gap-1.5 ${applies ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500"}`}
                >
                  <span className="font-mono font-bold">{f.family}</span>
                  <span className="tabular-nums">{f.standards_applicable}/{f.standards_total}</span>
                  <span className="text-[10px] text-muted-foreground">({f.requirements_applicable}/{f.requirements_total} reqs)</span>
                </button>
              );
            })}
          </div>

          {/* Per-standard detail (collapsible) */}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs underline text-muted-foreground hover:text-foreground"
            data-testid="scoping-toggle-detail"
          >
            {expanded ? "Hide" : "Show"} per-standard detail
          </button>
          {expanded && (
            <div className="border border-border" data-testid="scoping-standards-detail">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-3 py-2 border-b border-border bg-secondary/40 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                <div>Standard</div>
                <div>Reason (if not applicable)</div>
                <div>Applicable</div>
                <div>State</div>
              </div>
              <ul className="divide-y divide-border max-h-[420px] overflow-y-auto">
                {(data.standards || [])
                  .filter((s) => Object.keys(famExpanded).length === 0 || famExpanded[s.family])
                  .map((s) => (
                    <li key={s.standard_id} className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-3 py-2 text-xs" data-testid={`scoping-std-${s.standard_id}`}>
                      <span className="font-mono font-bold w-24 truncate">{s.standard_id}</span>
                      <span className="text-muted-foreground min-w-0 truncate" title={s.reason}>{s.reason || (s.state === "all" ? "Fully in scope" : s.state === "partial" ? "Partial — see requirements" : "—")}</span>
                      <span className="tabular-nums text-right">{s.applicable_count}/{s.total_count}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${
                        s.state === "all" ? "border-emerald-300 bg-emerald-50 text-emerald-700" :
                        s.state === "partial" ? "border-amber-300 bg-amber-50 text-amber-700" :
                        s.state === "empty" ? "border-slate-300 bg-slate-50 text-slate-400" :
                        "border-rose-300 bg-rose-50 text-rose-700"
                      }`}>{s.state}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
